import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface RegisterRequest {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  position?: string;
}

export interface RegisterResponse {
  message: string;
  redirectUrl: string;
}

export interface MessageResponse {
  message: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  ok: boolean;
  errorCode?: string;
  continueUrl?: string;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ChangePasswordResponse {
  ok: boolean;
  errorCode?: string;
}

/**
 * Thin wrapper around /api/auth/* — these are the same REST endpoints the
 * Thymeleaf forms use, so no backend changes required.
 *
 * Same-origin: the SPA is served from the auth-server itself, so cookies and
 * the `auth/` URL prefix work without CORS and without `withCredentials`.
 */
@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private http = inject(HttpClient);
  private base = '/api/auth';

  register(req: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${this.base}/register`, req);
  }

  login(req: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.base}/login`, req);
  }

  confirmEmail(token: string): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(`${this.base}/confirm-email`, { token });
  }

  resendConfirmation(email: string): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(`${this.base}/resend-confirmation`, { email });
  }

  forgotPassword(email: string): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(`${this.base}/forgot-password`, { email });
  }

  resetPassword(token: string, password: string): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(`${this.base}/reset-password`, { token, password });
  }

  changePassword(req: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    return this.http.post<ChangePasswordResponse>(`${this.base}/change-password`, req);
  }

  /** Returns 204 when the caller has a valid auth-server session, 401 otherwise. */
  checkSession(): Observable<void> {
    return this.http.get<void>(`${this.base}/session`);
  }

  /** Always 200; tells SPA whether session is active + где продолжить. */
  getSessionInfo(): Observable<SessionInfo> {
    return this.http.get<SessionInfo>(`${this.base}/session/info`);
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.base}/logout`, null);
  }
}

export interface SessionInfo {
  authenticated: boolean;
  email?: string;
  continueUrl: string;
}
